import { encode } from './csv';

type Filter = {
    isDisabled?: boolean;
    isNot?: boolean;
    key: string;
    values: string | string[];
};

const FLAG_DISABLED = '!';
const FLAG_NEGATED = '~';
const FILTER_PREFIX = 'f';

const buildRows = (filters: Filter[]): string[][] => {
    const rows: string[][] = [];
    for (const { key, values, isNot, isDisabled } of filters) {
        if (values.length > 0) {
            rows.push([
                (isDisabled ? FLAG_DISABLED : '') + (isNot ? FLAG_NEGATED : ''),
                key,
                ...values,
            ]);
        }
    }
    return rows;
};

const serializeFilters = (filters: Filter[]): string => {
    return encode(buildRows(filters));
};

export const setPresetsInSearchParams = (
    searchParams: URLSearchParams,
    presets: { property?: string; value?: string[] | null }[]
): void => {
    const filters: Filter[] =
        presets
            ?.filter((preset) => preset !== undefined && preset !== null)
            .map(
                (preset): Filter => ({
                    key: preset?.property ?? '',
                    values:
                        (Array.isArray(preset.value as string | string[])
                            ? preset?.value
                            : [preset.value as unknown as string]) ?? [],
                })
            ) ?? [];

    const serializedFilters = serializeFilters(filters);

    if (serializedFilters.length === 0) {
        searchParams.delete(FILTER_PREFIX);
    } else {
        searchParams.set(FILTER_PREFIX, serializedFilters);
    }

    searchParams.sort();
};

export type PropertyFilter = {
    /**
     * List of filters to apply to the charts in the dashboard, in the form of a JSON array. Each element
     * is a single filter for a default or user-defined dimension or custom property.
     */
    sources?:
        | ({
              /**
               * Default: `false`
               *
               * Flag that indicates how the filter should operate. If true, data that matches the criteria
               * is excluded from charts; otherwise, data that matches the criteria is included.
               */
              NOT?: boolean | null;
              /**
               * Example: `host`
               *
               * Name of the dimension or custom property to match to the data.
               *
               * Note: If the dimension or custom property doesn't exist in any of the charts for the
               * dashboard, and `ChartsFilter.NOT` is `true`, the system doesn't display any data in the
               * charts.
               */
              property: string;
              /**
               * Example:
               * ```
               * [
               *   "host5",
               *   "host6"
               * ]
               * ```
               *
               * A list of values to compare to the value of the dimension or custom property specified in
               * `ChartsFilter.property`. If the list contains more than one value, the filter becomes a set
               * of queries between the value of `property` and each element of value. The system joins these
               * queries with an implicit OR.
               */
              value: string[];
          } | null)[]
        | null;
    /**
     * Time range to show in all of the charts in the dashboard, in the form of a JSON object. The
     * specifications in this object override those in each individual chart. If you don't specify
     * values, the system defaults to using the individual time setting for each chart.
     */
    time?: {
        /**
         * Example: `1647936000000`
         *
         * The start of the time range to show for all charts in the dashboard. If you don't specify a
         * value for this property, the system defaults to the value of the `start` property for each chart.
         *
         * Note: The specified type for `start` is `string`, but the system also accepts a 64-bit signed
         * integer >= 0 that represents a time in *nix time in milliseconds.
         */
        start?: number | string | null;
        /**
         * Example: `1647943200000`
         *
         * The end of the time range to show for all charts in the dashboard. If you don't specify a value
         * for this property, the system defaults to the value of the `end` property for each chart.
         *
         * Note: The specified type for `end` is `string`, but the system also accepts a 64-bit signed
         * integer >= 0 that represents a time in *nix time in milliseconds.
         */
        end?: number | string | null;
    } | null;
    /**
     * List of UI-oriented filters that appear at the top of the dashboard, in the form of a JSON array
     * of JSON objects. For each filter you can specify a name and a label for the dimension or custom
     * property to filter against, a list of suggested comparison values to display in the UI, and other
     * properties that control the UI display. To see a more detailed description of the options, see the
     * property descriptions for the `ChartsWebUIFilter` model.
     */
    variables?:
        | ({
              /**
               * Example: `datacenter`
               *
               * Name of a custom property or dimension to filter against.
               *
               * Note: If the dimension or custom property doesn't exist in any of the charts in the
               * dashboard, the system doesn't display any data in the charts.
               */
              property: string;
              /**
               * Example:
               * ```
               * [
               *   "London",
               *   "Paris"
               * ]
               * ```
               *
               * A list of values to compare to the value of the dimension or custom property specified in
               * `ChartsWebUIFilter.property`. If the list contains more than one value, the filter becomes a
               * set of queries between the value of `property` and each element of `value`. The system joins
               * these queries with an implicit OR.
               *
               * Note: Not optional according to backend API docs, but found to be optional in practice.
               */
              value?: string[] | null;
              /**
               * Example: `Europe`
               *
               * A label for the filter. The system displays this label in the area preceding the input
               * textarea for the filter in the UI.
               *
               * Note: A good way to indicate that the string is a label is to append a space and a colon
               * (":") to it.
               */
              alias?: string;
              /**
               * Example:
               * ```
               * [
               *   "Europe",
               *   "Asia"
               * ]
               * ```
               *
               * List of strings containing values to place at the top of the **Suggested Values** dropdown
               * in UI for the dashboard, in the form of a JSON array. If `ChartsWebUIFilter.restricted` is
               * `true`, the filter must use one of the values in this array; users only have a choice of
               * selecting a value from this array.
               */
              preferredSuggestions?: string[] | null;
              /**
               * Default: `false`
               *
               * Example: `true`
               *
               * Flag that controls the display of chart data. If `true`, users must use this filter in order
               * to see data; otherwise, users can delete the filter.
               */
              required?: boolean | null;
              /**
               * Default: `false`
               *
               * Example: `true`
               *
               * Flag that controls the values allowed in the filter. If `true`, the only allowable values are
               * those specified in the `ChartsWebUIFilter.preferredSuggestions` array; otherwise, any value
               * is allowed.
               */
              restricted?: boolean | null;
          } | null)[]
        | null;
} | null;
